import Big from "big.js";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import Vue from "vue";
import uuidv4 from "uuid/v4";
import { BaleYardLot } from "@/store/modules/baleYardLots";
import { CALC_POUNDS_PER_TONS } from "@/constants";
import { GrindingLoadBaleYardLotLineItem } from "@/store/modules/grindingLoadBaleYardLotLineItem";
import { JSConn } from "@/utilities/js-conn";
import { formatTotal } from "@/utilities/display-helpers";
import { GrindingLoadLineItem } from "@/store/modules/grindingLoadLineItems";

export default Vue.extend({
  name: "grindingLoadEditForm",
  components: {
    FormErrors,
    LoadingAnimation,
  },
  props: {
    grindingLoadId: String,
  },
  data() {
    return {
      dataLoaded: false,
      loadingMessage: "Loading Grinding Load...",
      hasFormErrors: false,
      loadSizePounds: 50000,
      cancelParams: { name: "grindingLoads" },
      formErrors: [],
      grindingLoad: {
        name: "",
        description: "",
        id: "",
        client: "Unknown Client",
        client_id: "",
        date: "2020-01-24",
        total_tons: 0,
        grinding_load_bale_yard_lot_line_items: <
          GrindingLoadBaleYardLotLineItem[]
        >[],
        grinding_load_line_items: <GrindingLoadLineItem[]>[],
      },
    };
  },
  created() {
    const jsConnBaleYardLots = new JSConn();
    let dataPromises = [
      this.$store.dispatch("needBaleYardLots", jsConnBaleYardLots),
    ];
    if (this.grindingLoadId === "new") {
      Promise.all(dataPromises).then(() => {
        this.grindingLoad = {
          id: uuidv4(),
          name: "This Grinding Load",
          description: "",
          client: "Unknown Client",
          client_id: "",
          date: "2020-01-24",
          total_tons: 0,
          grinding_load_bale_yard_lot_line_items: [],
          grinding_load_line_items: [
            {
              id: "cdfe21ae-7f2c-4a4e-8664-31b69e6bce20",
              description: "Freight",
              line_item: "Freight tons",
              line_item_id: "b80d93ee-99ec-4b75-8eaf-f9994b0827bb",
              rate: 12.26,
              calc_rate: new Big(12.26),
            },
            {
              id: "b5011829-53e7-4b07-a927-69768182633c",
              description: "Grinding",
              line_item: "Grinding",
              line_item_id: "b0663976-9c6c-41e9-855b-dba1476a4258",
              rate: 10.26,
              calc_rate: new Big(10.26),
            },
            {
              id: "90983180-1595-4779-9279-360d96c3e815",
              description: "Grinding Sale Profit",
              line_item: "Grinding",
              line_item_id: "1003f61e-b554-49fe-8d04-95307552c3f5",
              rate: 8.0,
              calc_rate: new Big(8.0),
            },
          ],
        };
        this.dataLoaded = true;
      });
    } else {
      // TODO look up this grinding load
      // and put in the details and data here
      // for the edit
      this.grindingLoad = {
        id: uuidv4(),
        name: "This Grinding Load",
        description: "",
        client: "Unknown Client",
        client_id: "",
        date: "2020-01-24",
        total_tons: 0,
        grinding_load_bale_yard_lot_line_items: [],
        grinding_load_line_items: [],
      };
      this.dataLoaded = true;
    }
  },
  computed: {
    baleYardLots(): BaleYardLot[] {
      return this.$store.getters.baleYardLots;
    },
  },
  methods: {
    submitForm(evt: any): void {
      evt.preventDefault();
    },
    autoLoad(): void {
      // take the total of the bale yard lots
      // get the percentage of each lot
      // figure out the total size of the load
      // take the percentage of each bale lot times the load
      // get the number of bales based on bale size
      // then round that number and add to the grinding load
      const totalBaleYardPounds: Big = this.baleYardLots.reduce(
        (accum, item) => {
          return accum.plus(
            item.calc_pounds_per_unit.times(item.calc_quantity),
          );
        },
        new Big(0),
      );

      const baleYardPercentage: {
        [id: string]: Big;
      } = this.baleYardLots.reduce((accum, item) => {
        return {
          [item.id]: item.calc_pounds_per_unit
            .times(item.calc_quantity)
            .div(totalBaleYardPounds),
          ...accum,
        };
      }, {});

      this.baleYardLots.map(byLot => {
        const calcPercentage = baleYardPercentage[byLot.id];
        const calcPounds = calcPercentage.times(new Big(this.loadSizePounds));
        const calcQuantity = calcPounds.div(byLot.calc_pounds_per_unit).round();

        this.grindingLoad.grinding_load_bale_yard_lot_line_items.push({
          bale_yard_lot: byLot.description,
          bale_yard_lot_id: byLot.id,
          calc_pounds_per_unit: byLot.calc_pounds_per_unit,
          calc_price: byLot.calc_price,
          calc_quantity: calcQuantity,
          grinding_load_id: this.grindingLoad.id,
          id: uuidv4(),
          lot_unit: byLot.lot_unit,
          lot_unit_id: byLot.lot_unit_id,
          pounds_per_unit: byLot.pounds_per_unit,
          price: byLot.price,
          quantity: parseFloat(calcQuantity.toString()),
        });
      });
    },
    subtractLotFromLoad(byLot: BaleYardLot): void {
      // search through the items to see if there is one with the id
      let lItem = this.grindingLoad.grinding_load_bale_yard_lot_line_items.find(
        (el: GrindingLoadBaleYardLotLineItem) => {
          return el.bale_yard_lot_id == byLot.id;
        },
      );

      if (lItem) {
        if (lItem.calc_quantity.lte(0)) {
          lItem.quantity = 0;
          lItem.calc_quantity = new Big(0);
        } else {
          lItem.quantity = lItem.quantity - 1;
          lItem.calc_quantity = lItem.calc_quantity.minus(new Big(1));
        }
      }
    },
    addLotToLoad(byLot: BaleYardLot): void {
      // search through the items to see if there is one with the id
      let lItem = this.grindingLoad.grinding_load_bale_yard_lot_line_items.find(
        (el: GrindingLoadBaleYardLotLineItem) => {
          return el.bale_yard_lot_id == byLot.id;
        },
      );

      if (lItem) {
        lItem.quantity = lItem.quantity + 1;
        lItem.calc_quantity = lItem.calc_quantity.plus(new Big(1));
      } else {
        this.grindingLoad.grinding_load_bale_yard_lot_line_items.push({
          bale_yard_lot: byLot.description,
          bale_yard_lot_id: byLot.id,
          calc_pounds_per_unit: byLot.calc_pounds_per_unit,
          calc_price: byLot.calc_price,
          calc_quantity: new Big(1),
          grinding_load_id: this.grindingLoad.id,
          id: uuidv4(),
          lot_unit: byLot.lot_unit,
          lot_unit_id: byLot.lot_unit_id,
          pounds_per_unit: byLot.pounds_per_unit,
          price: byLot.price,
          quantity: 1,
        });
      }
    },
    calcTotalTons(): Big {
      return this.calcTotalPounds().div(CALC_POUNDS_PER_TONS);
    },
    calcTotalPounds(): Big {
      return this.grindingLoad.grinding_load_bale_yard_lot_line_items.reduce(
        (accum, item) => {
          return item.calc_quantity
            .times(item.calc_pounds_per_unit)
            .plus(accum);
        },
        new Big(0),
      );
    },
    calcCostPerTon(): Big {
      if (this.calcTotalTons().eq(new Big(0))) {
        return new Big(0);
      } else {
        return this.calcTotalCost().div(this.calcTotalTons());
      }
    },
    calcTotalCost(): Big {
      return this.grindingLoad.grinding_load_bale_yard_lot_line_items.reduce(
        (accum, item) => {
          return item.calc_quantity
            .times(item.calc_pounds_per_unit)
            .div(CALC_POUNDS_PER_TONS)
            .times(item.calc_price)
            .plus(accum);
        },
        new Big(0),
      );
    },
    calcLineItemPounds(lItem: GrindingLoadBaleYardLotLineItem): Big {
      return lItem.calc_quantity.times(lItem.calc_pounds_per_unit);
    },
    calcLineItemCost(lItem: GrindingLoadBaleYardLotLineItem): Big {
      return this.calcLineItemPounds(lItem)
        .div(CALC_POUNDS_PER_TONS)
        .times(lItem.calc_price);
    },
    formatTotal(numDigits: number, total: Big): string {
      return formatTotal(numDigits, total);
    },
    calcLoadLineItemCost(lItem: GrindingLoadLineItem): Big {
      return lItem.calc_rate.times(this.calcTotalTons());
    },
    calcTotalOtherCost(): Big {
      return this.grindingLoad.grinding_load_line_items.reduce(
        (accum, item) => {
          return accum.plus(item.calc_rate.times(this.calcTotalTons()));
        },
        new Big(0),
      );
    },
    calcTotalLoadCost(): Big {
      return this.calcTotalCost().plus(this.calcTotalOtherCost());
    },
    calcTotalLoadCostPerTon(): Big {
      if (this.calcTotalTons().eq(new Big(0))) {
        return new Big(0);
      } else {
        return this.calcTotalLoadCost().div(this.calcTotalTons());
      }
    },
  },
});
